/**
 * Sanitizador Genérico de Dados
 * 
 * Centraliza a sanitização genérica de dados, especialmente para
 * contextos como HMAC logging onde campos específicos precisam ser mascarados.
 * 
 * @module data.sanitizer
 */

/**
 * Sanitiza dados removendo campos sensíveis específicos
 * 
 * Remove ou mascara campos sensíveis mantendo a estrutura do objeto:
 * - Credenciais SMB (username, password)
 * - Dados de autenticação (signature, mas mantém nonce e timestamp)
 * - Tokens de sessão
 * - Chaves HMAC
 * 
 * @param data - Dados a serem sanitizados
 * @returns Dados sanitizados com campos sensíveis mascarados
 * 
 * @example
 * ```typescript
 * sanitizeData({
 *   smb: { username: 'user', password: 'pass' },
 *   auth: { signature: 'sig123', nonce: 'nonce', timestamp: 123 },
 *   sessionToken: 'token123'
 * })
 * // {
 * //   smb: { username: '[REDACTED]', password: '[REDACTED]' },
 * //   auth: { signature: '[REDACTED]', nonce: 'nonce', timestamp: 123 },
 * //   sessionToken: '[REDACTED]'
 * // }
 * ```
 */
export function sanitizeData(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }

  const sanitized: any = { ...data };

  // Remover campos sensíveis mas manter estrutura
  if (sanitized.smb) {
    sanitized.smb = {
      username: sanitized.smb.username ? '[REDACTED]' : undefined,
      password: sanitized.smb.password ? '[REDACTED]' : undefined
    };
  }

  if (sanitized.auth) {
    sanitized.auth = {
      signature: sanitized.auth.signature ? '[REDACTED]' : undefined,
      nonce: sanitized.auth.nonce,
      timestamp: sanitized.auth.timestamp
    };
  }

  if (sanitized.sessionToken) {
    sanitized.sessionToken = '[REDACTED]';
  }

  if (sanitized.hmacSecret) {
    sanitized.hmacSecret = '[REDACTED]';
  }

  // Sanitizar recursivamente objetos aninhados
  for (const key in sanitized) {
    if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  }

  return sanitized;
}

